// Pure helpers for a conversational turn: what we send (open fields), and what
// we render back (receipts). Kept out of the provider so both are unit-testable
// without a network or a React tree.

import { Answer, FormFill, OpenFieldSpec } from '../../API/types';
import { FillReceipt } from '../Shared/API/ApiTypes';
import { CfRegistry } from './formsConfig';
import { CfField, CfFieldType, CfForm, FieldValue } from './types';
import { displayValue, normalize } from './valueFormat';

/** Serialize open fields for the extraction contract: id, type, label, options,
 *  so the service can return canonical values (option values, not labels).
 *  `sensitive` and `noAI` ride along because the service drops guardrail fields
 *  before the message reaches the model. */
export const toOpenFieldSpecs = (fields: CfField[]): OpenFieldSpec[] =>
    fields.map((f) => ({
        id: f.id,
        type: f.type,
        label: f.label,
        options: f.options ?? [],
        optional: f.optional,
        sensitive: f.sensitive === true,
        noAI: f.noAI === true,
    }));

/**
 * How a turn resolved, for the three surfaces it can produce.
 *
 * - `ok` — reply, plus receipts for anything filled. A produced answer is
 *   `ok` even when a *retryable* `extraction_error` rides it — an answer is
 *   never masked by an error the server itself says wrote nothing (platform
 *   amendment, store tracker 6.55). See `fillsRetryable` for the quiet
 *   fills-retry affordance that error still earns.
 * - `retryable` — the turn produced no usable answer at all, and re-running
 *   the identical message is honest: a failed call, a timeout, or an
 *   extraction error alongside an empty/absent answer. This is the ONLY
 *   outcome that earns the full-failure "Try again" card — reserved for a
 *   degraded turn with nothing to show.
 * - `unrecorded` — the answer arrived but extraction failed in a way retrying
 *   cannot fix. The reply is shown normally with a notice, and no retry action,
 *   because an identical internal failure fails identically.
 */
export type TurnOutcome = 'ok' | 'retryable' | 'unrecorded';

export const classifyTurn = (result: Answer | null | undefined): TurnOutcome => {
    if (!result || !result.answer) return 'retryable';
    const error = result.extraction_error;
    if (!error) return 'ok';
    // Only an explicit `retryable: false` earns the non-retry surface. Every
    // other shape — `retryable: true`, no flag, a null, a future field — is
    // `ok` here: the answer stood, so the failure is never treated as a
    // silent success by discarding it, but it is also never allowed to mask
    // a real reply (bug cluster D, store tracker 6.55). `fillsRetryable`
    // below decides whether that failure earns its own quiet affordance.
    return error.retryable === false ? 'unrecorded' : 'ok';
};

/**
 * Does this turn's produced answer carry its own quiet fills-retry
 * affordance? True only when the answer stood (never for a turn with no
 * answer at all — that is the full-failure `retryable` outcome above) and the
 * extraction error is safely re-runnable per the platform's guarantee:
 * `retryable !== false` — the same forgiving default `classifyTurn` uses, so
 * an error whose shape is unrecognized still earns the affordance rather than
 * silently dropping it.
 */
export const fillsRetryable = (result: Answer | null | undefined): boolean => {
    if (!result?.answer) return false;
    const error = result.extraction_error;
    if (!error) return false;
    return error.retryable !== false;
};

export type NormalizedFill = {
    fieldId: string;
    value: FieldValue;
    /**
     * The turn form's own definition, or null when the turn filled a field of
     * another form — a different registry form, or one this widget holds no
     * definition for at all. Non-own fills are display-only: they render as
     * receipts so the fill is never invisible (labels resolve from the registry
     * when it knows the field), but they never reach `values`, progress,
     * next-required, or the open-field list.
     */
    field: CfField | null;
    /** Label to render: the local definition's, or the payload's own. */
    label: string;
    /** No value is available to store or show (withheld by contract, or absent). */
    withheld: boolean;
    /** Render the field's name without its value. */
    sensitive: boolean;
    /** The payload's value as sent, used to render a fill we cannot format
     *  locally. Empty for a field we hold a definition for. */
    rawValue: string;
};

/** The fills that carry a value this form can actually commit. Foreign fills
 *  are excluded: they belong to a form this widget does not own. */
export const committableFills = (fills: NormalizedFill[]): NormalizedFill[] =>
    fills.filter((f) => !f.withheld && f.field !== null);

/** Did this turn touch a field of *our* form? Drives implicit activation, which
 *  a foreign form's fill must not trigger. */
export const hasLocalFill = (fills: NormalizedFill[]): boolean =>
    fills.some((f) => f.field !== null);

/** A foreign value has no local type to format against, so it renders as the
 *  payload sent it. */
const rawDisplay = (value: FormFill['field_value']): string =>
    Array.isArray(value) ? value.join(', ') : String(value ?? '');

/** The registry field that owns `fieldId`, searched outside `exclude` — how a
 *  fill for a non-active registry form resolves its label and display format. */
const registryFieldFor = (
    registry: CfRegistry | null | undefined,
    fieldId: string,
    exclude: CfForm | null
): CfField | null => {
    if (!registry) return null;
    for (const other of registry.forms) {
        if (other === exclude) continue;
        const match = other.fields.find((f) => f.id === fieldId);
        if (match) return match;
    }
    return null;
};

/**
 * Match a turn's wire fills back to field definitions and normalize each value
 * into storage format. `form` is the form the turn ran for (the active form,
 * or the config form before anything starts) — only its fills are committable.
 *
 * A fill naming a field with no definition in `form` is kept rather than
 * dropped: the platform receipts requirement is that a fill is never invisible
 * to the visitor, and the bot legitimately fills forms beyond the active one.
 * When another *registry* form owns the field, its definition supplies the
 * label and display formatting (multiform slice); a truly foreign fill renders
 * from the payload's own `label` and `field_value`. Neither goes any further.
 */
export const normalizeFills = (
    form: CfForm | null,
    fills: FormFill[] | undefined,
    registry?: CfRegistry | null
): NormalizedFill[] => {
    if (!fills?.length) return [];
    return fills.map((fill) => {
        const field = form?.fields.find((f) => f.id === fill.field_id) ?? null;
        const registryField = field ? null : registryFieldFor(registry, fill.field_id, form);
        // Sensitivity can come from either side: the payload flags it on the
        // turn path, and a local definition may mark a field the payload did not.
        const sensitive =
            fill.sensitive === true || field?.sensitive === true || registryField?.sensitive === true;
        // A withheld fill arrives with `field_value` omitted by contract. There
        // is nothing to store, but the receipt still has to name the field.
        const withheld = fill.field_value == null;
        const value = withheld || !field
            ? ''
            : normalize(field, fill.field_value as FieldValue);
        return {
            fieldId: fill.field_id,
            value,
            field,
            // Falls back to the id only if no definition and no payload label —
            // a bare id reads poorly, but an unnamed fill would be worse.
            label: field?.label || registryField?.label || fill.label || fill.field_id,
            withheld,
            sensitive,
            rawValue: field
                ? ''
                : registryField && !withheld
                    ? displayValue(registryField, normalize(registryField, fill.field_value as FieldValue))
                    : rawDisplay(fill.field_value),
        };
    });
};

/**
 * Receipts for the turn's message. A sensitive field names itself and omits the
 * value — the platform forbids echoing it into the chat history. A foreign
 * field renders from the payload, styled identically: the visitor has no reason
 * to care which form a fill belongs to.
 */
export const toReceipts = (fills: NormalizedFill[]): FillReceipt[] =>
    fills.map(({ fieldId, field, value, label, withheld, sensitive, rawValue }) => ({
        fieldId,
        label,
        display: withheld || sensitive ? '' : field ? displayValue(field, value) : rawValue,
        sensitive: withheld || sensitive,
    }));

/**
 * Field types whose storage value is a *list* rather than a scalar. This is
 * the read-side twin of `toSaveFields`'s `Array.isArray(value) ? value.join(',')`
 * — the wire format itself is unchanged (out of scope: a separate cross-repo
 * contract change), so a value that went out comma-joined comes back
 * comma-joined and must be split back into an array on the way in. Only
 * types that are genuinely multi-valued in storage format belong here —
 * `valueFormat.normalize`/`displayValue` and `formsSelectors.isStructurallyComplete`
 * treat exactly `checklist` and `consent` as arrays of option ids; every other
 * type (including free text like `address`/`longtext`, where a comma is just
 * a comma — "Doe, John") stores and reads as a single string. `group` also
 * stores as an array, but its members are instance objects, not option ids —
 * splitting it on `,` would be nonsense, so it is deliberately excluded here.
 * (`toSaveFields` DOES currently stringify a `group` value onto the wire —
 * `Array.isArray` is true for it too, so it joins to
 * `"[object Object],[object Object]"` — a known, separately-tracked defect,
 * store tracker 6.34g, owned by the `forms-multi-instance-values` change and
 * deliberately out of scope here; `toSaveFields` is not touched by this fix.)
 */
const MULTI_VALUED_FIELD_TYPES: ReadonlySet<CfFieldType> = new Set(['checklist', 'consent']);

/**
 * Split a comma-joined multi-select value back into its option tokens,
 * option-aware — never a naive `.split(',')`. Checklist/consent options are
 * tenant-authored free text (`formsConfig.ts`'s `toOptions` sets
 * `value = String(entry)` straight from `allowed_values`; consent falls back
 * to `allowed_values` too when `attestations` is absent), so an option's own
 * value routinely contains a comma ("Yes, I want info"). A blind split
 * turns one selection into two, nothing renders checked, and — because
 * checklist has no `isStructurallyComplete` branch — `statusOf` still reads
 * `confirmed`, so the corruption is invisible until the next write re-joins
 * the broken pieces and overwrites the server's value.
 *
 * Matched greedily, longest option first, so an option that is a prefix of
 * another option (`"Yes"` / `"Yes, please"`) never steals a match that
 * belongs to the longer one. A run of characters that matches no option at
 * all is handled by the no-match branch below — the one place this function
 * has to choose between two invariants that can conflict: recovering a
 * padded real option (`" Band , Debate ,Chess"`, a legacy or externally
 * written row) versus never rewriting a value it cannot fully account for
 * (`"Unknown, Thing"`, `checklist` being open to `openForAI` extraction
 * means `valueFormat.matchOption` can hand back arbitrary unmatched text).
 * Both are real; the resolution is "split only when doing so is entirely
 * accounted for by known options" — see below.
 */
const splitMultiValue = (raw: string, options: CfField['options']): string[] => {
    const values = (options ?? []).map((o) => o.value).filter((v) => v !== '');
    if (!values.length) {
        return raw.split(',').map((p) => p.trim()).filter((p) => p !== '');
    }
    // The whole-string case first: a single selection whose own value
    // happens to contain a comma must never be treated as two tokens.
    if (values.includes(raw)) return [raw];

    const byLengthDesc = [...values].sort((a, b) => b.length - a.length);
    const tokens: string[] = [];
    let remaining = raw;
    while (remaining.length > 0) {
        // Snapshot per iteration: `remaining` is reassigned below, and a
        // closure over the mutable binding trips no-loop-func even though
        // `.find` runs synchronously within this same iteration.
        const current = remaining;
        const match = byLengthDesc.find(
            (v) => current.startsWith(v) && (current.length === v.length || current[v.length] === ',')
        );
        if (match) {
            tokens.push(match);
            remaining = remaining.slice(match.length);
            if (remaining.startsWith(',')) remaining = remaining.slice(1);
        } else {
            // Nothing known matches at the current position — comma-split
            // just this unmatched remainder to LOOK for a rescue, but only
            // ACT on it if every resulting piece is independently a known
            // option. That all-or-nothing bar is deliberate, not the
            // obvious "keep whichever pieces match": a comma inside an
            // unmatched remainder is ambiguous — it might be a real
            // separator between two selections, or it might just be part of
            // one literal unrecognized value ("Unknown, Thing" is one thing
            // a tenant typed, not two). A recognizable word turning up
            // inside an otherwise-unmatched string ("Xyz,Band") is not
            // evidence the comma is a separator — splitting on that partial
            // a signal is exactly the kind of guess that corrupted a value
            // one write later before this fix. Only when EVERY piece checks
            // out as a real option is the comma provably a separator and
            // not part of anyone's literal text — and only then is it safe
            // to trim (recovering the padded-value case). Any other outcome
            // keeps the remainder as ONE token, byte-for-byte as received,
            // so a write that merely re-sends it can never silently rewrite
            // it.
            const pieces = remaining.split(',').map((p) => p.trim()).filter((p) => p !== '');
            if (pieces.length > 0 && pieces.every((p) => values.includes(p))) {
                pieces.forEach((p) => tokens.push(p));
            } else {
                tokens.push(remaining);
            }
            remaining = '';
        }
    }
    return tokens;
};

/**
 * Normalize one collected-values entry (`CollectedFieldValue.value`) into
 * storage `FieldValue`, undoing `toSaveFields`'s comma-join for the types
 * that need it. Used by both hydration paths (mount read and
 * hydrate-on-activation) so a consent/checklist field never renders
 * unchecked, and `statusOf`/submit never see a bogus single-string value,
 * after a reload.
 *
 * - No field definition (a value for a field this widget no longer knows) —
 *   returned as-is; there is no type to key the decision on.
 * - Single-valued field — returned as-is. An already-array shape (unexpected,
 *   but the wire type technically allows it) collapses to its first member
 *   rather than being silently dropped.
 * - Multi-valued field, raw already an array (server sent one — the wire
 *   type allows it even though `toSaveFields` never produces it) — trimmed
 *   and re-filtered, not re-split (already discrete, nothing to tokenize).
 * - Multi-valued field, raw a string — tokenized option-aware via
 *   `splitMultiValue` (see above), empties dropped. `''` yields `[]`, not
 *   `['']`.
 */
export const hydrateFieldValue = (
    field: CfField | null | undefined,
    raw: string | string[] | null | undefined
): FieldValue => {
    if (raw == null) return '';
    if (!field || !MULTI_VALUED_FIELD_TYPES.has(field.type)) {
        return Array.isArray(raw) ? String(raw[0] ?? '') : raw;
    }
    if (Array.isArray(raw)) return raw.map((p) => p.trim()).filter((p) => p !== '');
    if (raw === '') return [];
    return splitMultiValue(raw, field.options);
};

export type SaveField = {
    fieldid: string;
    fieldvalue: string;
    chatorigin: boolean;
    sensitive: boolean;
};

/**
 * Full field list for the save path — the submit contract overwrites wholesale,
 * so every known value is re-sent on each commit (platform: "clients MAY
 * re-submit the full field list on each confirm").
 *
 * `chatOriginIds` names only the fields *this* write is confirming, so the
 * backend emits one `FieldFilledViaChat` per confirmed suggestion at the write.
 * Fields confirmed by an earlier submit are re-sent unmarked — marking them
 * again would count the same fill twice. `sensitive` comes from the form
 * definition and rides every submit, so the event can name a sensitive field
 * without echoing its value.
 */
export const toSaveFields = (
    form: CfForm | null,
    values: Record<string, FieldValue>,
    chatOriginIds?: ReadonlySet<string>
): SaveField[] => {
    if (!form) return [];
    return form.fields
        .filter((f) => values[f.id] !== undefined)
        .map((f) => {
            const value = values[f.id];
            return {
                fieldid: f.id,
                fieldvalue: Array.isArray(value) ? value.join(',') : String(value ?? ''),
                chatorigin: chatOriginIds?.has(f.id) === true,
                sensitive: f.sensitive === true,
            };
        });
};

// There is deliberately no stream end-event reader here. The platform clause for
// receipts-on-`/questionstream` has no target: the deployed backend serves no
// streaming route (`conversational-forms-backend` recorded it as dormant, dead
// code), so a conversational turn always resolves through `/question/`.
